/**
 * SSO Service
 * Servicio principal que maneja toda la lógica de SSO
 * Principios: Single Responsibility, Dependency Inversion
 */

import { SSOConfig, markSSOAttempted, buildSSOUrl } from './SSOConfig';
import { SSOValidator } from './SSOValidator';

/**
 * Resultado del intento de SSO
 * @typedef {Object} SSOResult
 * @property {boolean} success - Si el SSO fue exitoso
 * @property {string} status - Estado del SSO
 * @property {string} message - Mensaje descriptivo
 * @property {Object} [user] - Datos del usuario (si exitoso)
 * @property {string} [accessToken] - Token de acceso
 * @property {string} [refreshToken] - Token de refresco
 * @property {Object} [error] - Información del error (si falló)
 */

export class SSOService {
  /**
   * Intenta autenticar automáticamente usando SSO basado en referer
   * Si detecta un referer válido, redirige al SSO automáticamente
   * @returns {Promise<{success: boolean, status: string, message: string}>}
   */
  static async attemptAutoLogin() {
    console.log('[SSO Service] � Attempting SSO auto-login...');

    try {
      // 1. Validar si se puede intentar SSO
      const canAttempt = SSOValidator.canAttemptSSO();
      
      if (!canAttempt.isValid) {
        console.log(`[SSO Service] ⚠️ Cannot attempt SSO: ${canAttempt.message}`);
        return {
          success: false,
          status: canAttempt.status,
          message: canAttempt.message,
        };
      }

      const { hostname, baseDomain, provider } = canAttempt.data;
      console.log(`[SSO Service] ✅ Referer detected: ${hostname}`);
      console.log(`[SSO Service] 📍 Base domain: ${baseDomain}`);
      console.log(`[SSO Service] 🔧 SSO Provider: ${provider}`);

      // 2. Marcar intento en progreso
      markSSOAttempted(SSOConfig.STATUS.PENDING);

      // 3. Redirigir al SSO (navegación directa, no retorna)
      console.log(`[SSO Service] 🔄 Redirecting to ${provider} SSO...`);
      this.redirectToSSO(baseDomain, provider);

      // Este código nunca se ejecutará porque window.location.href redirige
      return {
        success: true,
        status: SSOConfig.STATUS.PENDING,
        message: 'Redirecting to SSO...',
      };

    } catch (error) {
      console.error('[SSO Service] ❌ Unexpected error during SSO:', error);
      markSSOAttempted(SSOConfig.STATUS.BACKEND_ERROR);
      
      return {
        success: false,
        status: SSOConfig.STATUS.BACKEND_ERROR,
        message: 'Unexpected error during SSO attempt',
        error: {
          message: error.message,
          stack: error.stack,
        },
      };
    }
  }    /**
   * Redirige al usuario al SSO mediante navegación directa
   * El backend responderá con HTTP 302/303 redirect al proveedor SSO
   * @param {string} baseDomain - Dominio base (ej: canvas.aulasneo.com)
   * @param {string} provider - Tipo de proveedor SSO
   * @returns {void} - No retorna nada porque hace navegación directa
   */
  static redirectToSSO(baseDomain, provider) {
    try {
      // Construir URL con parámetros según el proveedor
      const url = buildSSOUrl(baseDomain, provider);
      
      console.log(`[SSO Service] 🎯 Redirect URL: ${url}`);
      
      // Navegación directa - el backend hará el redirect al SSO del proveedor
      window.location.href = url;
    } catch (error) {
      console.error('[SSO] Error al construir URL de redirect:', error);
      throw error;
    }
  }

  /**
   * Limpia los intentos de SSO (útil para logout o testing)
   */
  static clearAttempts() {
    try {
      localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
      localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_FAILED);
      localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_SUCCESS);
      console.log('[SSO Service] 🧹 SSO attempts cleared');
    } catch (error) {
      console.warn('[SSO Service] Error clearing attempts:', error);
    }
  }

  /**
   * Obtiene el estado actual del SSO
   * @returns {Object|null} Estado del SSO o null
   */
  static getSSOStatus() {
    try {
      const attempted = localStorage.getItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
      if (!attempted) return null;

      return JSON.parse(attempted);
    } catch (error) {
      console.warn('[SSO Service] Error getting status:', error);
      return null;
    }
  }
}
